import { __decorate, __metadata, __param } from "tslib";
import { inject, injectable } from 'inversify';
import { Symbols } from '@/symbols';
import { E_ENABLED_STATUS } from './CommonOptionsService.type';
import { PQL_FREQUENCY, PQL_OPERATION, PQL_LOGIC, SEND_MODE, PQL_STEP, PQL_AVC_KEY, PQL_TAG_STATIUS, RuleTimeEffectiveType } from '@/apis/service/PqlRuleManagementApi.type';
import _ from 'lodash';
function makeDefaultRuleSetting() {
    return {
        // 规则id
        id: '',
        // 规则名称
        rule_name: '',
        // 产品线,
        product_line_id: '',
        // 规则激活状态
        status: E_ENABLED_STATUS.INACTIVE,
        // 规则频率
        frequency: undefined,
        // 规则发送方式
        send_mode: [],
        content_dingtalk_cn: '',
        content_dingtalk_en: '',
        lead_source: '',
        filter_list: [],
        tagSendMode: {
            tag_type: PQL_TAG_STATIUS.OPPORTUNITY,
            title_cn: '',
            title_en: '',
            tooltip_cn: '',
            tooltip_en: '',
            link_status: E_ENABLED_STATUS.INACTIVE,
            code: '',
        },
        email_template: [],
        time_effective_type: RuleTimeEffectiveType.T1,
    };
}
let PqlRuleManagementService = class PqlRuleManagementService {
    constructor(pqlRuleManagementApi, selfRegisterLinkService, i18n, systemService, productLineService) {
        this.pqlRuleManagementApi = pqlRuleManagementApi;
        this.selfRegisterLinkService = selfRegisterLinkService;
        this.i18n = i18n;
        this.systemService = systemService;
        this.productLineService = productLineService;
        this.getPQLRuleManagementList = this.pqlRuleManagementApi.getPQLRuleManagementList;
        this.getPQLRuleDetail = this.pqlRuleManagementApi.getPQLRuleDetail;
        this.getPQLRuleDataAnalysisList = this.pqlRuleManagementApi.getPQLRuleDataAnalysisList;
        this.getPQLRuleFilterFieldProperty = this.pqlRuleManagementApi.getPQLRuleFilterFieldProperty;
        this.addPQLRule = this.pqlRuleManagementApi.addPQLRule;
        this.editPQLRule = this.pqlRuleManagementApi.editPQLRule;
        this.editStatus = this.pqlRuleManagementApi.editStatus;
        this.checkPQLRuleName = this.pqlRuleManagementApi.checkPQLRuleName;
        this.preRunRule = this.pqlRuleManagementApi.preRunRule;
        this.deletePQLRule = this.pqlRuleManagementApi.deletePQLRule;
        this.getPqLRuleDataAnalysisList = this.pqlRuleManagementApi.getPQLRuleDataAnalysisList;
        // 规则编辑
        this.step = PQL_STEP.STEP_ONE;
        this.productId = '';
        // 条件数限制
        this.conditionsLimit = 20;
        // 推荐线索来源
        this.leadSourceList = [];
        // 指标列表
        this.fieldProperty = [];
        this.ruleSetting = makeDefaultRuleSetting();
        // 修改缓存结构为嵌套对象
        this._t0FieldProperty = {};
        this._t1FieldProperty = {};
    }
    get currentProductLine() {
        return this.productLineService.getCurrentProductLine(this.productId);
    }
    get productLineId() {
        return _.find(this.productLineService.productLine, line => line.product_line_key === this.productId)?.id;
    }
    /**
     * 状态：$t('backoffice.common.enabledOfTrue') 失效
     */
    get statusOptions() {
        return [
            { text: this.i18n.t('backoffice.common.enabledOfTrue'), value: E_ENABLED_STATUS.ACTIVE },
            { text: this.i18n.t('backoffice.common.enabledOfFalse'), value: E_ENABLED_STATUS.INACTIVE }
        ];
    }
    get LinkStatusOptions() {
        return [
            { text: this.i18n.t('backoffice.common.yes'), value: E_ENABLED_STATUS.ACTIVE },
            { text: this.i18n.t('backoffice.common.no'), value: E_ENABLED_STATUS.INACTIVE }
        ];
    }
    get frequencyOptions() {
        return [
            { text: this.i18n.t('enum.E_ResourceCyclical.DAILY'), value: PQL_FREQUENCY.DAILY },
            { text: this.i18n.t('enum.E_ResourceCyclical.WEEKLY'), value: PQL_FREQUENCY.WEEKLY },
            { text: this.i18n.t('enum.E_ResourceCyclical.MONTHLY'), value: PQL_FREQUENCY.MONTHLY }
        ];
    }
    get sendModeOptions() {
        return this.systemService.IsCn
            ? [
                { text: this.i18n.t('backoffice.ruleManagement.APIInfo'), value: SEND_MODE.API },
                { text: this.i18n.t('backoffice.ruleManagement.dingTalk'), value: SEND_MODE.DINGTALK },
                { text: this.i18n.t('backoffice.ruleManagement.tag'), value: SEND_MODE.TAG },
                { text: this.i18n.t('backoffice.ruleManagement.emailGroup'), value: SEND_MODE.EMAIL }
            ]
            : [
                { text: this.i18n.t('backoffice.ruleManagement.APIInfo'), value: SEND_MODE.API },
                { text: this.i18n.t('backoffice.ruleManagement.tag'), value: SEND_MODE.TAG },
                { text: this.i18n.t('backoffice.ruleManagement.emailGroup'), value: SEND_MODE.EMAIL }
            ];
    }
    getSendModeName(sendMode) {
        return _.find(this.sendModeOptions, i => i.value === sendMode)?.text || '-';
    }
    get LogicOptions() {
        return [
            { text: 'AND', value: PQL_LOGIC.AND },
            { text: 'OR', value: PQL_LOGIC.OR },
            { text: 'NOT', value: PQL_LOGIC.NOT }
        ];
    }
    get OperationOptions() {
        return [
            { text: '=', value: PQL_OPERATION.EQUAL },
            { text: '>', value: PQL_OPERATION.GREATER_THAN },
            { text: '<', value: PQL_OPERATION.LESS_THAN },
            { text: '>=', value: PQL_OPERATION.GREATER_THAN_OR_EQUAL },
            { text: '<=', value: PQL_OPERATION.LESS_THAN_OR_EQUAL },
            { text: '!=', value: PQL_OPERATION.NOT_EQUAL }
        ];
    }
    get TextOperationOptions() {
        return [
            { text: '=', value: PQL_OPERATION.EQUAL },
            { text: '!=', value: PQL_OPERATION.NOT_EQUAL }
        ];
    }
    get TagStatusOptions() {
        return [
            { text: this.i18n.t('backoffice.ruleManagement.risk'), value: PQL_TAG_STATIUS.RISK },
            { text: this.i18n.t('backoffice.ruleManagement.Opportunity'), value: PQL_TAG_STATIUS.OPPORTUNITY },
            { text: this.i18n.t('backoffice.ruleManagement.other'), value: PQL_TAG_STATIUS.OTHER }
        ];
    }
    getProductLine() {
        const product_line_type = this.productLineService.productLineType.RULE_CENTER;
        return this.productLineService.getProductLine(product_line_type);
    }
    postProductLine(product_line_key) {
        const params = {
            product_line_key: product_line_key,
            product_line_type: this.productLineService.productLineType.RULE_CENTER,
        };
        return this.productLineService.postProductLine(params);
    }
    getLogicColor(logic) {
        const logicColorMap = {
            [PQL_LOGIC.AND]: 'color: #DC7C08',
            [PQL_LOGIC.OR]: 'color: #1976D2',
            [PQL_LOGIC.NOT]: 'color: #D11A32',
        };
        return logicColorMap[logic] || '';
    }
    next() {
        this.step = PQL_STEP.STEP_TWO;
    }
    pre() {
        this.step = PQL_STEP.STEP_ONE;
    }
    createConditionItem() {
        return {
            field_logical_connector: PQL_LOGIC.AND,
            field_name: '',
            field_key: '',
            field_type: undefined,
            field_compare_expression: undefined,
            field_compare_value: undefined,
        };
    }
    // 新增condition
    addCondition(index) {
        this.ruleSetting.filter_list[index].filter_field_list.push(this.createConditionItem());
    }
    // 删除condition
    deleteCondition(filterIndex, index) {
        this.ruleSetting.filter_list[filterIndex].filter_field_list.splice(index, 1);
        // 如果condition里面的规则为0条  那么去掉这条filter
        if (this.ruleSetting.filter_list[filterIndex].filter_field_list.length === 0) {
            this.ruleSetting.filter_list.splice(filterIndex, 1);
            this.ruleSetting.filter_list[0].filter_logical_connector = undefined;
        }
        else {
            this.ruleSetting.filter_list[filterIndex].filter_field_list[0].field_logical_connector = undefined;
        }
    }
    addFilter() {
        const filterLogic = this.ruleSetting.filter_list.length === 0 ? undefined : PQL_LOGIC.AND;
        const filterItem = {
            filter_logical_connector: filterLogic,
            filter_field_list: [this.createConditionItem()],
        };
        this.ruleSetting.filter_list.push(filterItem);
    }
    getLeadSource(source) {
        return this.systemService.IsCn ? _.find(this.leadSourceList, item => item.value === source.campaign_id).key : source.campaign_promotion;
    }
    filterSettingDataSendModeAPI(mode) {
        return {
            send_mode: mode,
            api_config: this.systemService.IsCn
                ? {
                    campaign_id: this.ruleSetting.lead_source,
                }
                : {
                    campaign_promotion: this.ruleSetting.lead_source,
                },
        };
    }
    filterSettingDataSendModeDingTalk(mode) {
        return {
            send_mode: mode,
            content_cn: this.ruleSetting.content_dingtalk_cn,
            content_en: this.ruleSetting.content_dingtalk_en,
        };
    }
    filterSettingDataSendModeTag(mode) {
        return {
            send_mode: mode,
            api_config: {
                title_cn: this.ruleSetting.tagSendMode.title_cn,
                title_en: this.ruleSetting.tagSendMode.title_en,
                tooltip_cn: this.ruleSetting.tagSendMode.tooltip_cn,
                tooltip_en: this.ruleSetting.tagSendMode.tooltip_en,
                link_status: this.ruleSetting.tagSendMode.link_status,
                code: JSON.stringify(this.ruleSetting.tagSendMode.code),
                tag_type: this.ruleSetting.tagSendMode.tag_type,
            },
        };
    }
    filterSettingDataSendModeEmail(mode) {
        return {
            send_mode: mode,
            email_template: this.ruleSetting.email_template,
        };
    }
    getFilterSettingDataParams(send_content_list) {
        return {
            rule: {
                id: this.ruleSetting.id || '',
                rule_name: this.ruleSetting.rule_name,
                product_line_id: this.ruleSetting.product_line_id,
                status: this.ruleSetting.status,
                frequency: this.ruleSetting.frequency,
                time_effective_type: this.ruleSetting.time_effective_type,
            },
            filter_list: this.ruleSetting.filter_list,
            send_content_list: send_content_list,
        };
    }
    filterSettingData() {
        const send_content_list = _.map(this.ruleSetting.send_mode, mode => {
            if (mode === SEND_MODE.API) {
                // 复制对象以避免直接修改原数组
                return this.filterSettingDataSendModeAPI(mode);
            }
            if (mode === SEND_MODE.DINGTALK) {
                // 复制对象以避免直接修改原数组
                return this.filterSettingDataSendModeDingTalk(mode);
            }
            if (mode === SEND_MODE.TAG) {
                return this.filterSettingDataSendModeTag(mode);
            }
            if (mode === SEND_MODE.EMAIL) {
                return this.filterSettingDataSendModeEmail(mode);
            }
        });
        // 提交数据整合
        return this.getFilterSettingDataParams(send_content_list);
    }
    // 获取指标列表 option （区分en/cn）
    updateFieldOption(field_key) {
        const option = _.find(this.fieldProperty, item => item.field_key === field_key);
        // ACV仅跟着region走
        if (field_key === PQL_AVC_KEY.ACV_THIS_YEAR || field_key === PQL_AVC_KEY.ACV_LAST_YEAR) {
            return this.systemService.IsCn ? option.field_option_cn : option.field_option_en;
        }
        return this.i18n.locale === 'en' ? option.field_option_en : option.field_option_cn;
    }
    async editRuleStatus(id, status) {
        const params = {
            id: id,
            status: status,
        };
        return await this.editStatus(params);
    }
    async getActivityPromotionProductPackageKey() {
        const { lead_source_dictionary, } = await this.selfRegisterLinkService.getActivityPromotionProductPackageKey();
        this.leadSourceList = lead_source_dictionary;
    }
    async getFieldProperty(productLineId, timeEffectiveType) {
        // 根据timeEffectiveType和productLineId判断使用哪个缓存
        if (timeEffectiveType === RuleTimeEffectiveType.T0 && this._t0FieldProperty[productLineId]?.length > 0) {
            this.fieldProperty = this._t0FieldProperty[productLineId];
            return;
        }
        if (timeEffectiveType === RuleTimeEffectiveType.T1 && this._t1FieldProperty[productLineId]?.length > 0) {
            this.fieldProperty = this._t1FieldProperty[productLineId];
            return;
        }
        // 如果没有缓存，则请求数据并缓存
        const data = await this.getPQLRuleFilterFieldProperty(productLineId, timeEffectiveType);
        this.fieldProperty = data;
        // 根据timeEffectiveType和productLineId缓存到对应的变量
        if (timeEffectiveType === RuleTimeEffectiveType.T0) {
            this._t0FieldProperty[productLineId] = data;
        }
        else if (timeEffectiveType === RuleTimeEffectiveType.T1) {
            this._t1FieldProperty[productLineId] = data;
        }
    }
    initPQLRuleDetail(data) {
        _.forEach(data.filter_list, filter => {
            _.forEach(filter.filter_field_list, item => {
                item.field_type = _.find(this.fieldProperty, field => field.field_key === item.field_key).field_type;
            });
        });
        this.ruleSetting.filter_list = data.filter_list || [];
        this.ruleSetting.id = data.rule.id;
        this.ruleSetting.rule_name = data.rule.rule_name;
        this.ruleSetting.status = data.rule.status;
        this.ruleSetting.frequency = data.rule.frequency;
        this.ruleSetting.time_effective_type = data.rule.time_effective_type || RuleTimeEffectiveType.T1;
        _.forEach(data.send_content_list, item => {
            this.ruleSetting.send_mode.push(item.send_mode);
            if (item.send_mode === SEND_MODE.API) {
                this.ruleSetting.lead_source = this.systemService.IsCn ? item.api_config.campaign_id : item.api_config.campaign_promotion;
            }
            if (item.send_mode === SEND_MODE.DINGTALK) {
                this.ruleSetting.content_dingtalk_cn = item.content_cn;
                this.ruleSetting.content_dingtalk_en = item.content_en;
            }
            if (item.send_mode === SEND_MODE.TAG) {
                this.ruleSetting.tagSendMode = {
                    ...item.api_config,
                    code: JSON.parse(item.api_config.code),
                };
            }
            if (item.send_mode === SEND_MODE.EMAIL) {
                this.ruleSetting.email_template = item.email_template;
            }
        });
    }
    async init(ruleId, productId) {
        // step 1 重置步骤和产品线id 获取必备资源 leadSourceList & 指标列表
        this.reset();
        this.productId = productId;
        this.ruleSetting.product_line_id = this.productLineId;
        this.ruleSetting.id = ruleId;
        if (this.leadSourceList.length === 0) {
            await this.getActivityPromotionProductPackageKey();
        }
        let data = null;
        if (ruleId) {
            data = await this.getPQLRuleDetail(ruleId);
        }
        // 由于productLineId会切, 所以每次都要重新获取
        await this.getFieldProperty(this.productLineId, data ? data.rule.time_effective_type : this.ruleSetting.time_effective_type);
        if (data) {
            this.initPQLRuleDetail(data);
        }
    }
    reset() {
        this.step = PQL_STEP.STEP_ONE;
        this.ruleSetting = makeDefaultRuleSetting();
    }
};
PqlRuleManagementService = __decorate([
    injectable(),
    __param(0, inject(Symbols.PqlRuleManagementApi)),
    __param(1, inject(Symbols.SelfRegisterLinkService)),
    __param(2, inject(Symbols.I18n)),
    __param(3, inject(Symbols.SystemService)),
    __param(4, inject(Symbols.ProductLineService)),
    __metadata("design:paramtypes", [Object, Object, Object, Object, Object])
], PqlRuleManagementService);
export { PqlRuleManagementService };
